import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Trash2, Gift, Building2 } from 'lucide-react';
import { money } from '../utils/money';

const API_BASE = '/api';

const BOOSTER_VENUES = ['W in the Sky', 'W Timber', 'W Factory'];

function Boosters({ salespersonId, month, personName, excludeFirstN, onBoosterChange }) {
  const { token, isSuperadmin, user } = useAuth();
  // A salesperson may key in their OWN boosters; superadmin may key in anyone's.
  const canEdit = isSuperadmin || (user?.salesperson_id != null && Number(user.salesperson_id) === Number(salespersonId));
  const [boosters, setBoosters] = useState([]);
  const [showType1Form, setShowType1Form] = useState(false);
  const [showType2Form, setShowType2Form] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [type1Form, setType1Form] = useState({
    invoice_number: '', venue: '', event_month: '', deposit_amount: ''
  });
  const [type2Form, setType2Form] = useState({
    invoice_number: '', venue: '', event_space_revenue: ''
  });

  useEffect(() => { fetchBoosters(); }, [salespersonId, month]);

  const fetchBoosters = async () => {
    try {
      const response = await axios.get(`${API_BASE}/boosters/${salespersonId}?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoosters(response.data);
    } catch (err) {
      console.error('Failed to fetch boosters:', err);
    }
  };

  const handleSubmitType1 = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await axios.post(`${API_BASE}/boosters/type1`, {
        salesperson_id: salespersonId, month, ...type1Form
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(response.data.message);
      setType1Form({ invoice_number: '', venue: '', event_month: '', deposit_amount: '' });
      setShowType1Form(false);
      fetchBoosters();
      if (onBoosterChange) onBoosterChange();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add booster');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitType2 = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await axios.post(`${API_BASE}/boosters/type2`, {
        salesperson_id: salespersonId, month, ...type2Form
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(response.data.message);
      setType2Form({ invoice_number: '', venue: '', event_space_revenue: '' });
      setShowType2Form(false);
      fetchBoosters();
      if (onBoosterChange) onBoosterChange();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add booster');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booster entry?')) return;
    try {
      await axios.delete(`${API_BASE}/boosters/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Entry deleted');
      fetchBoosters();
      if (onBoosterChange) onBoosterChange();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  // Admin rejects a pending self-entered booster. The row is kept (marked
  // rejected) so the salesperson can see it was reviewed and declined; it stays
  // unverified so it never counts toward commission.
  const handleReject = async (id) => {
    if (!window.confirm('Reject this booster entry?')) return;
    try {
      await axios.put(`${API_BASE}/boosters/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Booster rejected');
      fetchBoosters();
      if (onBoosterChange) onBoosterChange();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  // Admin approves (or revokes) a pending self-entered booster so it counts toward commission.
  const handleVerify = async (id, verified) => {
    try {
      await axios.put(`${API_BASE}/boosters/${id}/verify`, { verified }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(verified ? 'Booster approved' : 'Approval revoked');
      fetchBoosters();
      if (onBoosterChange) onBoosterChange();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update approval');
    }
  };

  const statusBadge = (entry) => {
    if (entry.verified) return <span className="badge success">Approved</span>;
    if (entry.rejected) return <span className="badge danger">Rejected</span>;
    return <span className="badge warning">Pending</span>;
  };

  // Action buttons shared by both booster tables.
  const rowActions = (entry) => (
    <>
      {isSuperadmin && !entry.verified && !entry.rejected && (
        <>
          <button className="btn-primary btn-sm" onClick={() => handleVerify(entry.id, true)} title="Approve">Approve</button>
          <button className="btn-danger btn-sm" onClick={() => handleReject(entry.id)} title="Reject">Reject</button>
        </>
      )}
      {isSuperadmin && entry.rejected && (
        <button className="btn-primary btn-sm" onClick={() => handleVerify(entry.id, true)} title="Approve this rejected entry">Approve</button>
      )}
      {isSuperadmin && entry.verified && (
        <button className="btn-sm" onClick={() => handleVerify(entry.id, false)} title="Revoke approval">Revoke</button>
      )}
      {(isSuperadmin || (canEdit && !entry.verified)) && (
        <button className="btn-delete" onClick={() => handleDelete(entry.id)} title="Delete">
          <Trash2 size={14} />
        </button>
      )}
    </>
  );

  const formatCurrency = (amount) => money(parseFloat(amount) || 0);

  const type1Entries = boosters.filter(b => b.booster_type === 1);
  const type2Entries = boosters.filter(b => b.booster_type === 2);
  // Totals reflect only APPROVED boosters — that's what flows to commission.
  const type1Total = type1Entries.filter(b => b.verified).reduce((sum, b) => sum + parseFloat(b.booster_earned || 0), 0);
  const type2Total = type2Entries.filter(b => b.verified).reduce((sum, b) => sum + parseFloat(b.booster_earned || 0), 0);

  // Show Type 2 Corporate Event Space for Ginny only (Zac has W Timber auto-calculated)
  const showType2 = personName === 'Ginny';

  return (
    <div className="boosters-section">
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error} <button className="alert-close" onClick={() => setError(null)}>&times;</button></div>}

      {/* Type 1: Wedding/Birthday Deposit Booster */}
      <div className="booster-block">
        <div className="booster-block-header">
          <h4><Gift size={18} /> Type 1 — Wedding/Birthday Deposit Booster</h4>
          {canEdit && (
            <button className="btn-primary btn-sm" onClick={() => setShowType1Form(!showType1Form)}>
              {showType1Form ? 'Cancel' : <><Plus size={14} /> Add Deposit</>}
            </button>
          )}
        </div>
        <p className="booster-rule">
          From {excludeFirstN + 1}{excludeFirstN + 1 === 2 ? 'nd' : excludeFirstN + 1 === 3 ? 'rd' : 'th'} deposit onwards
          &nbsp;|&nbsp; 7% (May-Sep events) &nbsp;|&nbsp; 4% (Oct-Dec events)
          &nbsp;|&nbsp; Venues: W in the Sky, W Timber, W Factory
        </p>

        {showType1Form && (
          <form className="booster-form" onSubmit={handleSubmitType1}>
            <div className="form-grid">
              <div className="form-group">
                <label>Invoice Number *</label>
                <input type="text" value={type1Form.invoice_number}
                  onChange={e => setType1Form({...type1Form, invoice_number: e.target.value})}
                  placeholder="e.g. IV2605-00001" required />
              </div>
              <div className="form-group">
                <label>Venue *</label>
                <select value={type1Form.venue}
                  onChange={e => setType1Form({...type1Form, venue: e.target.value})} required>
                  <option value="">Select venue...</option>
                  {BOOSTER_VENUES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Event Scheduled Month *</label>
                <input type="month" value={type1Form.event_month}
                  onChange={e => setType1Form({...type1Form, event_month: e.target.value})}
                  min="2026-05" max="2027-12" required />
              </div>
              <div className="form-group">
                <label>Deposit Amount (RM) *</label>
                <input type="number" step="0.01" min="0" value={type1Form.deposit_amount}
                  onChange={e => setType1Form({...type1Form, deposit_amount: e.target.value})}
                  placeholder="e.g. 6000.00" required />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Adding...' : 'Add Deposit'}
              </button>
            </div>
          </form>
        )}

        {type1Entries.length > 0 && (
          <div className="table-container">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Invoice</th>
                  <th>Venue</th>
                  <th>Event Month</th>
                  <th>Deposit</th>
                  <th>Rate</th>
                  <th>Booster</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {type1Entries.map(entry => (
                  <tr key={entry.id} className={entry.deposit_sequence <= excludeFirstN ? 'excluded' : ''}>
                    <td>{entry.deposit_sequence}</td>
                    <td><strong>{entry.invoice_number}</strong></td>
                    <td>{entry.venue}</td>
                    <td>{entry.event_month}</td>
                    <td>{formatCurrency(entry.deposit_amount)}</td>
                    <td>
                      {entry.deposit_sequence <= excludeFirstN
                        ? <span className="badge warning">Excluded</span>
                        : `${(parseFloat(entry.booster_rate) * 100).toFixed(1)}%`}
                    </td>
                    <td className={parseFloat(entry.booster_earned) > 0 ? 'amount-positive' : ''}>
                      {formatCurrency(entry.booster_earned)}
                    </td>
                    <td>{statusBadge(entry)}</td>
                    <td>{rowActions(entry)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="6"><strong>Type 1 Total (approved)</strong></td>
                  <td className="amount-positive"><strong>{formatCurrency(type1Total)}</strong></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {type1Entries.length === 0 && !showType1Form && (
          <p className="booster-empty">No deposit boosters this month.</p>
        )}
      </div>

      {/* Type 2: Corporate Event Space Booster (Ginny only) */}
      {showType2 && (
        <div className="booster-block">
          <div className="booster-block-header">
            <h4><Building2 size={18} /> Type 2 — Corporate Event Space Booster (1%)</h4>
            {canEdit && (
              <button className="btn-primary btn-sm" onClick={() => setShowType2Form(!showType2Form)}>
                {showType2Form ? 'Cancel' : <><Plus size={14} /> Add Event</>}
              </button>
            )}
          </div>
          <p className="booster-rule">
            1% on delivered corporate event space revenue &nbsp;|&nbsp; No minimum, no cap
          </p>

          {showType2Form && (
            <form className="booster-form" onSubmit={handleSubmitType2}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Invoice Number *</label>
                  <input type="text" value={type2Form.invoice_number}
                    onChange={e => setType2Form({...type2Form, invoice_number: e.target.value})}
                    placeholder="e.g. IV2605-00001" required />
                </div>
                <div className="form-group">
                  <label>Venue</label>
                  <input type="text" value={type2Form.venue}
                    onChange={e => setType2Form({...type2Form, venue: e.target.value})}
                    placeholder="e.g. W Factory" />
                </div>
                <div className="form-group full-width">
                  <label>Event Space Revenue (RM) *</label>
                  <input type="number" step="0.01" min="0" value={type2Form.event_space_revenue}
                    onChange={e => setType2Form({...type2Form, event_space_revenue: e.target.value})}
                    placeholder="e.g. 20000.00" required />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Adding...' : 'Add Event'}
                </button>
              </div>
            </form>
          )}

          {type2Entries.length > 0 && (
            <div className="table-container">
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Venue</th>
                    <th>Event Space Revenue</th>
                    <th>Rate</th>
                    <th>Booster</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {type2Entries.map(entry => (
                    <tr key={entry.id}>
                      <td><strong>{entry.invoice_number}</strong></td>
                      <td>{entry.venue}</td>
                      <td>{formatCurrency(entry.event_space_revenue)}</td>
                      <td>1.0%</td>
                      <td className="amount-positive">{formatCurrency(entry.booster_earned)}</td>
                      <td>{statusBadge(entry)}</td>
                      <td>{rowActions(entry)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td colSpan="4"><strong>Type 2 Total (approved)</strong></td>
                    <td className="amount-positive"><strong>{formatCurrency(type2Total)}</strong></td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {type2Entries.length === 0 && !showType2Form && (
            <p className="booster-empty">No event space boosters this month.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default Boosters;
